/**
 * Maps human-readable keys to Framer XLIFF translation unit IDs.
 * Used by the pricing page to look up translations from messages/{locale}.json.
 *
 * To find new keys: search messages/en-CH.json for the English text,
 * then add the Framer ID here.
 */
export const pricingKeys = {
  // Hero
  pageTitle: "pageTitle",
  label: "label",
  heading: "heading",
  usp1: "usp1",
  usp2: "usp2",
  usp3: "usp3",

  // Answer-first summary (AHD-9930) — CH only, see _components/pricing-answer.tsx
  answerHeading: "answerHeading",
  answerBody: "answerBody",
  answerInsurance: "answerInsurance",
  answerInsuranceLink: "answerInsuranceLink",

  // FAQ (CMS-driven, FAQs tagged `pricing`)
  faqHeading: "faqHeading",

  // MRI section
  mriHeading: "mriHeading",
  mriDescription: "mriDescription",

  // Add-ons
  imagingAddonsHeading: "imagingAddonsHeading",
  imagingAddonsDescription: "imagingAddonsDescription",

  // Advisory CTA
  advisoryHeading: "advisoryHeading",
  advisoryButton: "advisoryButton",

  // Blood section
  bloodHeading: "bloodHeading",
  bloodDescription: "bloodDescription",

  // Blood add-ons
  bloodAddonsHeading: "bloodAddonsHeading",
  bloodAddonsDescription: "bloodAddonsDescription",

  // Advisory subtext
  advisorySubtext: "advisorySubtext",

  // Insurance
  insuranceHeading: "insuranceHeading",
  insuranceDescription: "insuranceDescription",

  // Package CTAs
  bookCore: "bookCore",
  bookAdvanced: "bookAdvanced",
  bookPro: "bookPro",

  // UI strings
  comparePackages: "comparePackages",
  viewBiomarkersList: "viewBiomarkersList",
  bookAdvancedBlood: "bookAdvancedBlood",
  advancedBloodTestTitle: "advancedBloodTestTitle",
  advancedBloodTestDesc: "advancedBloodTestDesc",

  // Imaging add-on titles + descriptions
  addonBodyComposition: "addonBodyComposition",
  addonBodyCompositionDesc: "addonBodyCompositionDesc",
  addonGenderHealth: "addonGenderHealth",
  addonGenderHealthDesc: "addonGenderHealthDesc",
  addonHeartCT: "addonHeartCT",
  addonHeartCTDesc: "addonHeartCTDesc",
  addonMammaMRI: "addonMammaMRI",
  addonMammaMRIDesc: "addonMammaMRIDesc",
  addonDEXA: "addonDEXA",
  addonDEXADesc: "addonDEXADesc",

  // Blood add-on titles + descriptions
  addonBVitamins: "addonBVitamins",
  addonBVitaminsDesc: "addonBVitaminsDesc",
  addonOmega: "addonOmega",
  addonOmegaDesc: "addonOmegaDesc",
  addonHormoneWomen: "addonHormoneWomen",
  addonHormoneWomenDesc: "addonHormoneWomenDesc",
  addonHormoneMen: "addonHormoneMen",
  addonHormoneMenDesc: "addonHormoneMenDesc",

  // Tab labels
  tabMriLabel: "tabMriLabel",
  tabBloodLabel: "tabBloodLabel",
  tabMembershipLabel: "tabMembershipLabel",
  // Mobile-only short variants — full labels don't fit in 3 columns on a
  // 390px viewport without truncating or horizontally scrolling.
  tabMriLabelShort: "tabMriLabelShort",
  tabBloodLabelShort: "tabBloodLabelShort",
  tabMembershipLabelShort: "tabMembershipLabelShort",

  // Package descriptions
  pkgCoreDescription: "pkgCoreDescription",
  pkgAdvancedDescription: "pkgAdvancedDescription",
  pkgProDescription: "pkgProDescription",

  // Package "includes" labels
  pkgCoreIncludesLabel: "pkgCoreIncludesLabel",
  pkgAdvancedIncludesLabel: "pkgAdvancedIncludesLabel",
  pkgProIncludesLabel: "pkgProIncludesLabel",

  // Package feature names (Core)
  featureFullBodyMri: "featureFullBodyMri",
  featureBrainMri: "featureBrainMri",
  featureSpineMri: "featureSpineMri",

  // Package feature names (Advanced)
  featureAdvancedBloodTest: "featureAdvancedBloodTest",
  featureBodyComposition: "featureBodyComposition",
  featureProstateOvariesMri: "featureProstateOvariesMri",

  // Package feature names (Pro)
  featureBrainVolumetry: "featureBrainVolumetry",
  featureHipsKneesMri: "featureHipsKneesMri",
  featureHormonesVitamins: "featureHormonesVitamins",

  // Package card labels
  pkgDurationLabel: "pkgDurationLabel",
  pkgNoRadiation: "pkgNoRadiation",
  pkgMostPopular: "pkgMostPopular",

  // InfoTooltip
  moreInfo: "moreInfo",

  // Trust signals (MRI cards)
  trustReviewedByPhysicians: "trustReviewedByPhysicians",
  trustPersonalActionPlan: "trustPersonalActionPlan",
  trustVideoConsult: "trustVideoConsult",

  // Trust signals (Blood card)
  trustAddTestsAnytime: "trustAddTestsAnytime",
  trustAddTestsAnytimeMri: "trustAddTestsAnytimeMri",
  trustInteractiveReport: "trustInteractiveReport",

  // Blood test card
  bloodDuration: "bloodDuration",
  bloodTestDesc: "bloodTestDesc",
  bloodIncludesLabel: "bloodIncludesLabel",
  bloodFeature1: "bloodFeature1",
  bloodFeature2: "bloodFeature2",
  bloodFeature3: "bloodFeature3",
  bloodFeature4: "bloodFeature4",
  bloodFeature5: "bloodFeature5",
  bloodFeature6: "bloodFeature6",
  // DE-only: the card sells the Vorsorge-Starter (prevention-starter, 67
  // markers), so /pricing and the DE city LPs override the shared 80+ blurb and
  // first bullet with these. Non-DE markets keep the shared strings above.
  preventionStarterCardDesc: "preventionStarterCardDesc",
  preventionStarterCardFeature1: "preventionStarterCardFeature1",

  // Comparison grid labels
  comparisonImaging: "comparisonImaging",
  comparisonBloodAnalysis: "comparisonBloodAnalysis",

  // Imaging comparison features
  compHead: "compHead",
  compHeadInfo: "compHeadInfo",
  compSpine: "compSpine",
  compSpineInfo: "compSpineInfo",
  compNeck: "compNeck",
  compNeckInfo: "compNeckInfo",
  compPelvis: "compPelvis",
  compPelvisInfo: "compPelvisInfo",
  compAbdomen: "compAbdomen",
  compAbdomenInfo: "compAbdomenInfo",
  compSpineMuscle: "compSpineMuscle",
  compSpineMuscleInfo: "compSpineMuscleInfo",
  compProstateOvaries: "compProstateOvaries",
  compProstateOvariesInfo: "compProstateOvariesInfo",
  compBodyComp: "compBodyComp",
  compBodyCompInfo: "compBodyCompInfo",
  compBrainAnalysis: "compBrainAnalysis",
  compBrainAnalysisInfo: "compBrainAnalysisInfo",
  compHipKnee: "compHipKnee",
  compHipKneeInfo: "compHipKneeInfo",

  // Blood comparison features
  compInterpretBlood: "compInterpretBlood",
  compAdvancedBlood: "compAdvancedBlood",
  compAdvancedBloodInfo: "compAdvancedBloodInfo",
  compHormoneTest: "compHormoneTest",
  compHormoneTestInfo: "compHormoneTestInfo",
  compBVitaminsTest: "compBVitaminsTest",
  compBVitaminsTestInfo: "compBVitaminsTestInfo",

  // Biomarker category names
  bioCatBloodCount: "bioCatBloodCount",
  bioCatLiver: "bioCatLiver",
  bioCatKidney: "bioCatKidney",
  bioCatLipid: "bioCatLipid",
  bioCatThyroid: "bioCatThyroid",
  bioCatDiabetes: "bioCatDiabetes",
  bioCatInflammation: "bioCatInflammation",
  bioCatIron: "bioCatIron",
  bioCatVitamins: "bioCatVitamins",
  bioCatTumour: "bioCatTumour",
  bioCatElectrolytes: "bioCatElectrolytes",
  bioCatCoagulation: "bioCatCoagulation",

  // Insurance section
  insuranceHeadingText: "insuranceHeadingText",
  insuranceDesc: "insuranceDesc",
  insuranceKptBloodMri: "insuranceKptBloodMri",
  insuranceKptCoverage: "insuranceKptCoverage",
  insuranceBookCheckup: "insuranceBookCheckup",
  insuranceTalkTeam: "insuranceTalkTeam",

  // Add-on combination notes
  imagingAddonsCombinationNote: "imagingAddonsCombinationNote",
  imagingAddonsPreConsultNote: "imagingAddonsPreConsultNote",
  bloodAddonsCombinationNote: "bloodAddonsCombinationNote",
} as const
